<template>
  <div class="nd-page patient-profile-page">
    <header class="nd-page-header">
      <div>
        <h1 class="page-title">Perfil do paciente</h1>
        <p class="nd-page-kicker">Histórico clínico, documentos e vínculos do paciente.</p>
      </div>
      <div class="patient-profile-actions">
        <v-btn outlined color="primary" elevation="0" to="/pacientes">
          <v-icon left size="18">mdi-arrow-left</v-icon>
          Pacientes
        </v-btn>
        <v-btn v-if="patient" color="primary" elevation="0" :to="`/pacientes/${patientId}`">
          <v-icon left size="18">mdi-pencil-outline</v-icon>
          Editar dados
        </v-btn>
      </div>
    </header>

    <template v-if="loading">
      <div class="patient-profile-stats">
        <v-skeleton-loader v-for="item in 4" :key="item" type="image" class="patient-profile-skeleton" />
      </div>
      <v-skeleton-loader type="card" class="mt-6" />
    </template>

    <template v-else-if="patient">
      <div class="patient-profile-stats">
        <div class="patient-profile-stat">
          <span>Consultas</span>
          <strong>{{ appointments.length }}</strong>
          <small>atendimentos registrados</small>
        </div>
        <div class="patient-profile-stat">
          <span>Prescrições</span>
          <strong>{{ prescriptions.length }}</strong>
          <small>orientações emitidas</small>
        </div>
        <div class="patient-profile-stat">
          <span>Exames</span>
          <strong>{{ exams.length }}</strong>
          <small>solicitações vinculadas</small>
        </div>
        <div class="patient-profile-stat" :class="{ 'patient-profile-stat--warning': allergies.length }">
          <span>Alergias</span>
          <strong>{{ allergies.length }}</strong>
          <small>{{ allergies.length ? 'atenção clínica' : 'nenhum alerta registrado' }}</small>
        </div>
      </div>

      <div class="patient-profile-layout">
        <v-card class="nd-section-card patient-profile-summary">
          <div class="patient-profile-summary__identity">
            <span class="patient-profile-avatar">{{ initials }}</span>
            <div>
              <h2>{{ patient.name }}</h2>
              <p>{{ patient.email || 'E-mail não informado' }}</p>
              <div class="patient-profile-chips">
                <span :class="['patient-profile-chip', patient.active ? 'is-active' : 'is-inactive']">
                  {{ patient.active ? 'Ativo' : 'Inativo' }}
                </span>
                <span v-if="patient.gender" class="patient-profile-chip">{{ getGenderLabel(patient.gender) }}</span>
              </div>
            </div>
          </div>

          <div class="patient-profile-details">
            <div class="patient-profile-detail">
              <v-icon size="18">mdi-card-account-details-outline</v-icon>
              <span>CPF</span>
              <strong>{{ formatCpf(patient.registration) }}</strong>
            </div>
            <div class="patient-profile-detail">
              <v-icon size="18">mdi-phone-outline</v-icon>
              <span>Telefone</span>
              <strong>{{ patient.phone ? formatPhone(patient.phone.value || patient.phone) : 'Não informado' }}</strong>
            </div>
            <div class="patient-profile-detail">
              <v-icon size="18">mdi-cake-variant-outline</v-icon>
              <span>Nascimento</span>
              <strong>{{ formatDate(patient.birthday) }}</strong>
            </div>
            <div class="patient-profile-detail">
              <v-icon size="18">mdi-map-marker-outline</v-icon>
              <span>Localização</span>
              <strong>{{ patientLocation }}</strong>
            </div>
          </div>

          <div v-if="isMinor" class="patient-profile-guardian">
            <div class="patient-profile-section-title">
              <v-icon size="18">mdi-account-child-outline</v-icon>
              Responsável
            </div>
            <div class="patient-profile-guardian__grid">
              <span>{{ patient.guardianName || 'Nome não informado' }}</span>
              <span>{{ patient.guardianCpf ? formatCpf(patient.guardianCpf) : 'CPF não informado' }}</span>
              <span>{{ patient.guardianPhone ? formatPhone(patient.guardianPhone) : 'Telefone não informado' }}</span>
            </div>
          </div>

          <div class="patient-profile-shortcuts">
            <v-btn text color="primary" :to="`/consultas/novo?patient=${patientId}`">
              <v-icon left size="18">mdi-calendar-plus-outline</v-icon>
              Nova consulta
            </v-btn>
            <v-btn text color="primary" :to="`/prescricoes/novo?patient=${patientId}`">
              <v-icon left size="18">mdi-receipt-text-plus-outline</v-icon>
              Nova prescrição
            </v-btn>
          </div>
        </v-card>

        <div class="patient-profile-content">
          <v-card class="nd-section-card patient-profile-section">
            <div class="patient-profile-section__header">
              <div>
                <h3>Histórico clínico</h3>
                <p>Consultas, prescrições, exames e registros clínicos vinculados.</p>
              </div>
            </div>

            <div class="patient-profile-history-grid">
              <section class="patient-profile-history-block">
                <div class="patient-profile-section-title">
                  <v-icon size="18">mdi-calendar-month-outline</v-icon>
                  Consultas
                  <span>{{ appointments.length }}</span>
                </div>
                <div v-if="appointments.length === 0" class="patient-profile-empty">Nenhuma consulta encontrada</div>
                <v-list v-else dense class="patient-profile-list">
                  <v-list-item v-for="a in appointments" :key="a.id" :to="`/consultas/${a.id}`">
                    <v-list-item-content>
                      <v-list-item-title>{{ a.procedure ? a.procedure.name : 'Consulta' }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ formatDate(a.scheduledDate) }}{{ a.scheduledTime ? ' às ' + a.scheduledTime : '' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip x-small :color="appointmentStatusColor(a.state).bg" :text-color="appointmentStatusColor(a.state).text">
                        {{ appointmentStatusLabel(a.state) }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </section>

              <section class="patient-profile-history-block">
                <div class="patient-profile-section-title">
                  <v-icon size="18">mdi-receipt-text-outline</v-icon>
                  Prescrições
                  <span>{{ prescriptions.length }}</span>
                </div>
                <div v-if="prescriptions.length === 0" class="patient-profile-empty">Nenhuma prescrição encontrada</div>
                <v-list v-else dense class="patient-profile-list">
                  <v-list-item v-for="p in prescriptions" :key="p.id" :to="`/prescricoes/${p.id}`">
                    <v-list-item-content>
                      <v-list-item-title>Prescrição #{{ p.id }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ p.items && p.items.length ? `${p.items.length} medicamento${p.items.length > 1 ? 's' : ''}` : 'Sem medicamentos' }}
                        {{ p.date ? ' • ' + formatDate(p.date) : '' }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </section>

              <section class="patient-profile-history-block">
                <div class="patient-profile-section-title">
                  <v-icon size="18">mdi-flask-outline</v-icon>
                  Exames
                  <span>{{ exams.length }}</span>
                </div>
                <div v-if="exams.length === 0" class="patient-profile-empty">Nenhum exame encontrado</div>
                <v-list v-else dense class="patient-profile-list">
                  <v-list-item v-for="e in exams" :key="e.id" :to="`/exames/${e.id}`">
                    <v-list-item-content>
                      <v-list-item-title>{{ e.type ? e.type.name : 'Exame' }}</v-list-item-title>
                      <v-list-item-subtitle>{{ e.laboratory ? e.laboratory.name : 'Laboratório não informado' }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-chip x-small :color="examStatusColor(e.status).bg" :text-color="examStatusColor(e.status).text">
                        {{ examStatusLabel(e.status) }}
                      </v-chip>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </section>

              <section class="patient-profile-history-block">
                <div class="patient-profile-section-title">
                  <v-icon size="18">mdi-alert-outline</v-icon>
                  Alergias
                  <span>{{ allergies.length }}</span>
                </div>
                <div v-if="allergies.length === 0" class="patient-profile-empty">Nenhuma alergia registrada</div>
                <v-list v-else dense class="patient-profile-list">
                  <v-list-item v-for="a in allergies" :key="a.id" :to="`/alergias/${a.id}`">
                    <v-list-item-content>
                      <v-list-item-title>{{ a.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="a.medicines && a.medicines.length">{{ a.medicines.join(', ') }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon size="18">mdi-chevron-right</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </section>
            </div>
          </v-card>

          <v-card class="nd-section-card patient-profile-section">
            <div class="patient-profile-section__header">
              <div>
                <h3>Planejamento e financeiro</h3>
                <p>Odontogramas, DSDs e orçamentos associados ao paciente.</p>
              </div>
            </div>

            <div class="patient-profile-resource-grid">
              <router-link class="patient-profile-resource" :to="odontograms.length ? `/odontogramas/${odontograms[0].id}` : '/odontogramas/novo'">
                <v-icon size="22">mdi-tooth-outline</v-icon>
                <span>Odontogramas</span>
                <strong>{{ odontograms.length }}</strong>
              </router-link>
              <router-link class="patient-profile-resource" :to="dsds.length ? `/dsd/${dsds[0].id}` : '/dsd/novo'">
                <v-icon size="22">mdi-image-outline</v-icon>
                <span>DSD</span>
                <strong>{{ dsds.length }}</strong>
              </router-link>
              <router-link class="patient-profile-resource" :to="proposals.length ? `/orcamentos/${proposals[0].id}` : '/orcamentos/novo'">
                <v-icon size="22">mdi-file-document-edit-outline</v-icon>
                <span>Orçamentos</span>
                <strong>{{ proposals.length }}</strong>
              </router-link>
            </div>
          </v-card>
        </div>
      </div>
    </template>

    <template v-else>
      <v-card class="nd-section-card patient-profile-not-found">
        <v-icon size="48">mdi-account-off-outline</v-icon>
        <h2>Paciente não encontrado</h2>
        <p>Não foi possível localizar o cadastro solicitado.</p>
        <v-btn color="primary" elevation="0" to="/pacientes">Voltar para pacientes</v-btn>
      </v-card>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PatientProfile',

  data: () => ({
    patient: null,
    allergies: [],
    appointments: [],
    exams: [],
    prescriptions: [],
    odontograms: [],
    dsds: [],
    proposals: [],
    loading: true,
  }),

  computed: {
    patientId() {
      return this.$route.params.id;
    },
    patientName() {
      return this.patient ? this.patient.name : '…';
    },
    initials() {
      if (!this.patient || !this.patient.name) return '?';
      const parts = this.patient.name.trim().split(' ').filter(Boolean);
      if (parts.length === 1) return parts[0][0].toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    isMinor() {
      if (!this.patient || !this.patient.birthday) return false;
      const birth = new Date(this.patient.birthday.split('T')[0] + 'T00:00:00');
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
      return age < 18;
    },
    patientLocation() {
      if (!this.patient || !this.patient.address) return 'Não informada';
      const city = this.patient.address.city;
      const state = this.patient.address.state;
      if (city && state) return `${city}, ${state}`;
      return city || state || this.patient.address.street || 'Não informada';
    },
  },

  created() {
    this.loadProfile();
  },

  methods: {
    async loadProfile() {
      this.loading = true;
      try {
        const [profileRes, odontogramRes, dsdRes, proposalRes] = await Promise.allSettled([
          axios.get(`/patients/${this.patientId}/profile`),
          axios.get('/odontogram', { params: { patient: this.patientId, start: 0, limit: 100 } }),
          axios.get('/dsd', { params: { start: 0, limit: 100 } }),
          axios.get('/proposals', { params: { patient: this.patientId, start: 0, limit: 100 } }),
        ]);

        if (profileRes.status === 'fulfilled') {
          const data = profileRes.value.data.data;
          this.patient = data.patient;
          this.allergies = data.allergies ?? [];
          this.appointments = data.appointments ?? [];
          this.exams = data.exams ?? [];
          this.prescriptions = data.prescriptions ?? [];
        } else {
          throw profileRes.reason;
        }

        this.odontograms = odontogramRes.status === 'fulfilled'
          ? (odontogramRes.value.data.data ?? []).filter(o => o.patient?.id == this.patientId || o.patientId == this.patientId)
          : [];

        this.dsds = dsdRes.status === 'fulfilled'
          ? (dsdRes.value.data.data ?? []).filter(d => d.patient?.id == this.patientId || d.patientId == this.patientId)
          : [];

        this.proposals = proposalRes.status === 'fulfilled'
          ? (proposalRes.value.data.data ?? [])
          : [];

      } catch (e) {
        this.$dialog.notify.error('Erro ao carregar perfil do paciente', { position: 'top-right', timeout: 4000 });
      } finally {
        this.loading = false;
      }
    },

    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val.includes('T') ? val : val + 'T00:00:00');
      return new Intl.DateTimeFormat('pt-BR').format(d);
    },

    formatCpf(val) {
      if (!val) return '—';
      const d = val.replace(/\D/g, '');
      if (d.length === 11) return `${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9)}`;
      return val;
    },

    formatPhone(val) {
      if (!val) return '—';
      const d = val.replace(/\D/g, '');
      if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
      return val;
    },

    getGenderLabel(gender) {
      const map = { 1: 'Feminino', 2: 'Masculino', 3: 'Não-Binário', 4: 'Prefiro não responder', 5: 'Outro' };
      return map[gender] ?? gender;
    },

    proposalStatusLabel(status) {
      const map = { 1: 'Pendente', 2: 'Aprovado', 3: 'Recusado', 4: 'Cancelado' };
      return map[status] ?? 'Pendente';
    },

    proposalStatusColor(status) {
      const map = {
        1: { bg: 'yellow lighten-4', text: '#CA8A04' },
        2: { bg: 'green lighten-5', text: '#16A34A' },
        3: { bg: 'red lighten-5', text: '#DC2626' },
        4: { bg: 'grey lighten-4', text: '#9CA3AF' },
      };
      return map[status] ?? { bg: 'yellow lighten-4', text: '#CA8A04' };
    },

    appointmentStatusLabel(state) {
      const map = { 1: 'Agendado', 2: 'Confirmado', 3: 'Concluído', 4: 'Cancelado' };
      return map[state] ?? 'Pendente';
    },

    appointmentStatusColor(state) {
      const map = {
        1: { bg: 'blue lighten-5', text: '#1D4ED8' },
        2: { bg: 'green lighten-5', text: '#16A34A' },
        3: { bg: 'deep-purple lighten-5', text: '#7C3AED' },
        4: { bg: 'red lighten-5', text: '#DC2626' },
      };
      return map[state] ?? { bg: 'yellow lighten-4', text: '#CA8A04' };
    },

    examStatusLabel(status) {
      const map = { PENDING: 'Pendente', COMPLETED: 'Concluído', NONE: '—' };
      return map[status] ?? status ?? '—';
    },

    examStatusColor(status) {
      if (status === 'COMPLETED') return { bg: 'green lighten-5', text: '#16A34A' };
      if (status === 'PENDING') return { bg: 'yellow lighten-4', text: '#CA8A04' };
      return { bg: 'grey lighten-4', text: '#9CA3AF' };
    },

  },
};
</script>

<style scoped>
.patient-profile-page {
  gap: 22px;
}

.patient-profile-actions {
  align-items: center;
  display: flex;
  gap: 10px;
}

.patient-profile-stats {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.patient-profile-skeleton {
  border-radius: 8px;
  min-height: 116px;
}

.patient-profile-stat {
  background: #f6f7f5;
  border: 1px solid rgba(15, 23, 42, 0.04);
  border-radius: 8px;
  min-height: 116px;
  padding: 22px 24px;
}

.patient-profile-stat span,
.patient-profile-detail span {
  color: #747d78;
  display: block;
  font-size: 13px;
  font-weight: 500;
}

.patient-profile-stat strong {
  color: #131a16;
  display: block;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  margin-top: 8px;
}

.patient-profile-stat small {
  color: #a64ad1;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-top: 10px;
}

.patient-profile-stat--warning small {
  color: #d97706;
}

.patient-profile-layout {
  align-items: start;
  display: grid;
  gap: 18px;
  grid-template-columns: 360px minmax(0, 1fr);
}

.patient-profile-summary,
.patient-profile-section {
  border-color: rgba(15, 23, 42, 0.08) !important;
  box-shadow: none !important;
}

.patient-profile-summary {
  padding: 24px;
  position: sticky;
  top: 92px;
}

.patient-profile-summary__identity {
  align-items: center;
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
}

.patient-profile-avatar {
  align-items: center;
  background: linear-gradient(135deg, #a64ad1, #7d3dcc);
  border-radius: 18px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 72px;
  font-size: 24px;
  font-weight: 800;
  height: 72px;
  justify-content: center;
  text-transform: uppercase;
}

.patient-profile-summary h2 {
  color: #131a16;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.15;
  margin: 0;
}

.patient-profile-summary p,
.patient-profile-section__header p {
  color: #747d78;
  font-size: 14px;
  line-height: 1.35;
  margin: 6px 0 0;
}

.patient-profile-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.patient-profile-chip {
  background: #f1e7fa;
  border-radius: 999px;
  color: #8f42c4;
  display: inline-flex;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 9px;
}

.patient-profile-chip.is-active {
  background: #e8f8ef;
  color: #168a4a;
}

.patient-profile-chip.is-inactive {
  background: #fdecec;
  color: #c24141;
}

.patient-profile-details {
  display: grid;
  gap: 10px;
}

.patient-profile-detail {
  align-items: center;
  background: #f8f9f7;
  border-radius: 8px;
  display: grid;
  gap: 3px 10px;
  grid-template-columns: 20px 1fr;
  padding: 13px 14px;
}

.patient-profile-detail .v-icon {
  color: #a64ad1;
  grid-row: span 2;
}

.patient-profile-detail strong {
  color: #131a16;
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.patient-profile-guardian {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  margin-top: 18px;
  padding-top: 18px;
}

.patient-profile-guardian__grid {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.patient-profile-guardian__grid span {
  background: #fff7ed;
  border-radius: 8px;
  color: #7c2d12;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 12px;
}

.patient-profile-shortcuts {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
}

.patient-profile-shortcuts .v-btn {
  justify-content: flex-start;
}

.patient-profile-content {
  display: grid;
  gap: 18px;
}

.patient-profile-section {
  padding: 0;
}

.patient-profile-section__header {
  align-items: center;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: space-between;
  padding: 22px 24px;
}

.patient-profile-section__header h3 {
  color: #131a16;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0;
}

.patient-profile-history-grid {
  display: grid;
  gap: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.patient-profile-history-block {
  min-height: 180px;
  padding: 22px 24px;
}

.patient-profile-history-block:nth-child(odd) {
  border-right: 1px solid rgba(15, 23, 42, 0.08);
}

.patient-profile-history-block:nth-child(n + 3) {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.patient-profile-section-title {
  align-items: center;
  color: #131a16;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  gap: 8px;
}

.patient-profile-section-title .v-icon {
  color: #a64ad1;
}

.patient-profile-section-title span {
  background: #f1e7fa;
  border-radius: 999px;
  color: #8f42c4;
  font-size: 11px;
  font-weight: 800;
  margin-left: auto;
  min-width: 22px;
  padding: 3px 7px;
  text-align: center;
}

.patient-profile-empty {
  align-items: center;
  background: #f8f9f7;
  border: 1px dashed rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  color: #8b948e;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  justify-content: center;
  margin-top: 16px;
  min-height: 96px;
  text-align: center;
}

.patient-profile-list {
  background: transparent !important;
  margin-top: 12px;
}

.patient-profile-list .v-list-item {
  background: #f8f9f7;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 8px;
  margin-top: 8px;
}

.patient-profile-list .v-list-item__title {
  color: #131a16;
  font-size: 14px;
  font-weight: 800;
}

.patient-profile-list .v-list-item__subtitle {
  color: #747d78 !important;
  font-size: 12px;
}

.patient-profile-resource-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 22px 24px;
}

.patient-profile-resource {
  align-items: center;
  background: #f8f9f7;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 8px;
  color: #131a16;
  display: grid;
  gap: 8px;
  min-height: 112px;
  padding: 18px;
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.patient-profile-resource:hover {
  border-color: rgba(166, 74, 209, 0.38);
  transform: translateY(-1px);
}

.patient-profile-resource .v-icon {
  color: #a64ad1;
}

.patient-profile-resource span {
  color: #747d78;
  font-size: 13px;
  font-weight: 600;
}

.patient-profile-resource strong {
  color: #131a16;
  font-size: 28px;
  font-weight: 800;
}

.patient-profile-not-found {
  align-items: center;
  color: #747d78;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 56px 24px;
  text-align: center;
}

.patient-profile-not-found .v-icon {
  color: #a64ad1;
}

.patient-profile-not-found h2 {
  color: #131a16;
  font-size: 24px;
  font-weight: 800;
  margin: 0;
}

.patient-profile-not-found p {
  margin: 0 0 8px;
}

@media (max-width: 1260px) {
  .patient-profile-layout {
    grid-template-columns: 1fr;
  }

  .patient-profile-summary {
    position: static;
  }
}

@media (max-width: 960px) {
  .patient-profile-actions,
  .patient-profile-section__header {
    align-items: stretch;
    flex-direction: column;
  }

  .patient-profile-stats,
  .patient-profile-history-grid,
  .patient-profile-resource-grid {
    grid-template-columns: 1fr;
  }

  .patient-profile-history-block,
  .patient-profile-history-block:nth-child(odd),
  .patient-profile-history-block:nth-child(n + 3) {
    border-right: 0;
    border-top: 1px solid rgba(15, 23, 42, 0.08);
  }

  .patient-profile-history-block:first-child {
    border-top: 0;
  }
}
</style>
